import { ITeam, ITeamType } from '../entities/team';
import { IUserFull } from '../entities/user';
import { TeamEditRequestBody } from '../entities/user_team_request';
import {
  IOrganizationsUsers,
  IOrganizationsUsersWithOptions,
  ResGetTeams,
  ResGetUsers,
  ResGetUsersTeam
} from '../types/api-types';
import { formatOptions } from '../utils';
import { BaseOrganizationEndpoints } from './base';
import { ResponseWithStatus } from './response/response_with_status';

const RESOLVE_PATH: Record<ITeamType, string> = {
  directory: ``,
  group: `/groups`,
  management: `/management`
};

export class TeamsClient extends BaseOrganizationEndpoints {
  async add(req: {
    name: string;
    type: ITeamType;
    parent_id: string | null;
    is_assignable?: boolean;
    is_hidden?: boolean;
    is_hidden_for_non_member?: boolean;
  }) {
    const path = `/v1/teams${RESOLVE_PATH[req.type]}`;
    return this.requestWithStatus<ITeam>('POST', path, req);
  }

  async delete({ id, type }: { type: ITeamType; id: string }) {
    const path = `/v1/teams${RESOLVE_PATH[type]}/${encodeURIComponent(id)}`;
    return this.requestWithStatus<ITeam>('DELETE', path);
  }

  async update(id: ITeam['id'], type: ITeamType, body: TeamEditRequestBody) {
    const path = `/v1/teams${RESOLVE_PATH[type]}/${encodeURIComponent(id)}`;
    return await this.requestWithStatus('PUT', path, body);
  }

  async all({ type, hidden }: { type: ITeamType; hidden?: boolean }) {
    const path = `/v1/teams${RESOLVE_PATH[type]}`;
    const urlSearchParams = new URLSearchParams();
    if (hidden) {
      urlSearchParams.append('hidden', '1');
    }
    const queryString = urlSearchParams.toString();
    return this.requestWithStatus<ResGetTeams>(
      'GET',
      path + (queryString ? `?${queryString}` : '')
    );
  }

  async members({
    id,
    query = null,
    type
  }: { id: ITeam['id']; query?: string | null; type: ITeamType }) {
    const path = `/teams${RESOLVE_PATH[type]}/${encodeURIComponent(id)}/users`;
    const urlSearchParams = new URLSearchParams();
    if (query) {
      urlSearchParams.set('query', query);
    }
    const queryString = urlSearchParams.toString();
    return this.requestWithStatus<ResGetUsers>(
      'GET',
      path + (queryString ? `?${queryString}` : '')
    );
  }

  async addMember({
    teamId,
    userId,
    type
  }: { teamId: ITeam['id']; userId: IUserFull['id']; type: ITeamType }) {
    const path = `/teams${RESOLVE_PATH[type]}/${encodeURIComponent(teamId)}/users/${encodeURIComponent(userId)}`;
    return this.requestWithStatus<ResGetUsersTeam>('POST', path);
  }

  async removeMember({
    teamId,
    userId,
    type
  }: { teamId: ITeam['id']; userId: IUserFull['id']; type: ITeamType }) {
    const path = `/teams${RESOLVE_PATH[type]}/${encodeURIComponent(teamId)}/users/${encodeURIComponent(userId)}`;
    return this.requestWithStatus<ResGetUsersTeam>('DELETE', path);
  }

  async forUser({
    userId,
    type
  }: { userId: IUserFull['id']; type: ITeamType }) {
    const path = `/v1/users/${encodeURIComponent(userId)}/teams${RESOLVE_PATH[type]}`;
    return this.requestWithStatus<ResGetTeams>('GET', path);
  }

  async managed({ query }: { query?: string | null } = {}) {
    const urlSearchParams = new URLSearchParams();
    if (query) {
      urlSearchParams.set('query', query);
    }
    const queryString = urlSearchParams.toString();
    return this.requestWithStatus<IOrganizationsUsers>(
      'GET',
      `/users/managed` + (queryString ? `?${queryString}` : '')
    );
  }

  async managedWithOptions(args: { query?: string | null }) {
    const res = await this.managed(args);
    if (res.ok && res.result) {
      const formatted = formatOptions<IOrganizationsUsers, IUserFull>(
        res.result
      );
      return { ...res, result: formatted };
    }
    return res as unknown as ResponseWithStatus<IOrganizationsUsersWithOptions>;
  }
}
